import './Table.scss';

import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TableSortLabelProps,
  Tooltip,
  Typography,
  Table as WebSharedTable,
} from '@trackman/web-shared-components';

import { ReactComponent as RightIcon } from 'svg/ArrowRight.svg';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { useTranslation } from 'react-i18next';
import { TableColumnUnitConfig } from 'utilities/tableHeaders';
import { useGetUnitSystem } from 'hooks/helperHooks';
import { displayDate, isValidIsoDate } from 'utilities/dateHelpers';

export type Order = TableSortLabelProps['direction'];

export const DEFAULT_ROWS_PER_PAGE = 5;

type Props<RowObject> = {
  rows: RowObject[];
  columns: TableColumn<RowObject>[];
  loading: boolean;
  noRowsLabel?: string;
  pageState: [number, (newPage: number) => void];
  orderState: [Order, (newOrder: Order) => void];
  orderByState: [keyof RowObject | '', (newOrderBy: keyof RowObject | '') => void];
  searchFilter?: string;
  onRowClick?: (row: RowObject) => void;
  onRowSelect?: (row: RowObject) => void;
  isRowSelected?: (row: RowObject) => boolean;
  emptyRowHeight?: string;
  noFooter?: boolean;
  tileTable?: boolean;
  skeletonRows?: number;
  renderExpandedRow?: (row: RowObject) => ReactNode;
};

type TableColumn<RowObject> = Partial<TableColumnUnitConfig> & {
  field: keyof RowObject;
  headerName: string;
  headerShortName?: string;
  width?: string;
  sortable?: boolean;
  searchable?: boolean;
  renderCell?: (cellValue: any, row: RowObject) => ReactNode;
};

const generateArray = (itemsCount: number) => Array.from(Array(itemsCount).keys());

const TableColumnHeader = ({ column }: { column: TableColumn<any> }) => {
  const unitSystem = useGetUnitSystem();
  const { t } = useTranslation();

  return (
    <>
      {column.headerShortName ? (
        <Tooltip title={column.headerName} placement='bottom' arrow>
          <div>
            {column.headerShortName}
            {column.headerUnitLabel && (
              <div style={{ fontSize: '10px', color: '#A0A0A0', fontWeight: 500 }}>
                {typeof column.headerUnitLabel === 'function' ? t(column.headerUnitLabel(unitSystem)) : t(column.headerUnitLabel)}
              </div>
            )}
          </div>
        </Tooltip>
      ) : (
        <div>
          {column.headerName}
          {column.headerUnitLabel && (
            <div style={{ fontSize: '10px', color: '#A0A0A0', fontWeight: 500 }}>
              {typeof column.headerUnitLabel === 'function' ? t(column.headerUnitLabel(unitSystem)) : t(column.headerUnitLabel)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const Table =
  // ensure that id is present, since it's used as a key
  <RowObject extends { id: string } & { [key in string]: any }>({
    rows,
    columns,
    loading,
    noRowsLabel,
    pageState,
    orderState,
    orderByState,
    searchFilter,
    onRowClick,
    onRowSelect,
    isRowSelected,
    emptyRowHeight = '47px',
    noFooter = false,
    tileTable = false,
    skeletonRows,
    renderExpandedRow,
  }: Props<RowObject>) => {
    const [orderBy, setOrderBy] = orderByState;
    const [order, setOrder] = orderState;
    const [page, setPage] = pageState;

    const { t } = useTranslation();

    const rowsFiltered = useMemo(() => {
      if (searchFilter === undefined) return rows;

      const searchableFields = columns.filter((column) => column.searchable).map((column) => column.field);

      return rows.filter((row) =>
        Object.entries(row)
          .filter(([field, value]) => searchableFields.includes(field))
          .map(([field, value]) => value?.toLowerCase() ?? '')
          .find((value) => value.includes(searchFilter.toLowerCase()))
      );
    }, [rows, searchFilter, columns]);

    const rowsDisplay = useMemo(
      () =>
        [...rowsFiltered]
          .sort((a, b) => {
            const orderByA = a[orderBy];
            const orderByB = b[orderBy];
            if (orderByA === null || orderByA === undefined || orderByB === null || orderByB === undefined) return 1;
            if (typeof orderByA === 'number' && typeof orderByB === 'number') return (order === 'asc' ? 1 : -1) * (orderByA - orderByB);
            if (typeof orderByA === 'string' && typeof orderByB === 'string')
              return (order === 'asc' ? 1 : -1) * orderByA.localeCompare(orderByB);
            return 0;
          }) // sorting
          .slice(
            tileTable ? undefined : page * DEFAULT_ROWS_PER_PAGE,
            tileTable ? undefined : page * DEFAULT_ROWS_PER_PAGE + DEFAULT_ROWS_PER_PAGE
          ), // pagination
      [rowsFiltered, order, orderBy, page, tileTable]
    );

    const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});
    const selectedRowRef = useRef<HTMLTableRowElement | null>(null);
    const currentlySelectedId = useMemo(() => {
      return rows.find((row) => isRowSelected?.(row))?.id || null;
    }, [rows, isRowSelected]);

    useEffect(() => {
      if (selectedRowRef.current) {
        selectedRowRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, [currentlySelectedId]);

    const handleRowExpansion = (rowId: string) => {
      setExpandedRowIds((prev) => ({
        ...prev,
        [rowId]: !prev[rowId],
      }));
    };

    return (
      <TableContainer
        style={
          tileTable
            ? { border: 0, borderRadius: 0, height: '100%', scrollbarColor: '#e0e0e0 transparent', scrollbarWidth: 'thin' }
            : undefined
        }
        className='Table-container'
      >
        <WebSharedTable style={tileTable ? { height: '100%' } : undefined}>
          <TableBody sx={{ position: 'relative' }}>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: 'fit-content',
                    border: 'none',
                  }}
                >
                  {noRowsLabel ?? t('No data')}
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <SkeletonRows
                rowsPerPage={skeletonRows ?? (tileTable ? 10 : DEFAULT_ROWS_PER_PAGE)}
                numOfColumns={columns.length}
                onRowClick={onRowClick}
                renderExpandedRow={renderExpandedRow}
                tileTable={tileTable}
              />
            ) : (
              <>
                {/* Display Rows */}
                {rowsDisplay.map((row) => {
                  const isExpanded = expandedRowIds[row.id];
                  const isSelected = isRowSelected?.(row);

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        ref={isSelected ? selectedRowRef : null}
                        onClick={() => {
                          onRowClick?.(row);
                          onRowSelect?.(row);
                          if (renderExpandedRow) {
                            handleRowExpansion(row.id);
                          }
                        }}
                        hover={!!onRowClick || !!onRowSelect || !!renderExpandedRow}
                        selected={isSelected}
                        sx={{ cursor: !!onRowClick || !!onRowSelect ? 'pointer' : 'auto' }}
                      >
                        {!!renderExpandedRow && (
                          <TableCell>
                            <Button
                              style={{
                                padding: '0 8px',
                                minWidth: 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowExpansion(row.id);
                              }}
                              icon={() => (
                                <RightIcon
                                  style={{
                                    transition: 'transform 150ms',
                                    transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
                                  }}
                                />
                              )}
                            ></Button>
                          </TableCell>
                        )}

                        {columns.map((column) => {
                          const displayCell = () => {
                            let cellValue = row[column.field];

                            if (cellValue == null) {
                              return '-';
                            }

                            if (column.renderCell) {
                              return column.renderCell(cellValue, row);
                            }

                            if (isValidIsoDate(cellValue)) {
                              return displayDate(cellValue);
                            }

                            if (typeof cellValue === 'number') {
                              const roundedValue =
                                column.fractionDigits !== undefined ? cellValue.toFixed(column.fractionDigits) : String(cellValue);
                              cellValue = roundedValue !== '0' && !column.leadingZero ? roundedValue.replace(/^0+/, '') : roundedValue;
                            }

                            return cellValue + (column.cellUnitLabel ? ' ' + column.cellUnitLabel : '');
                          };
                          return (
                            <TableCell
                              key={`row-${row.id}-${String(column.field)}-${column.headerName}`}
                              style={
                                tileTable
                                  ? {
                                      padding: '4px 12px',
                                      height: 'auto',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }
                                  : undefined
                              }
                            >
                              <Typography component='span'>{displayCell()}</Typography>
                            </TableCell>
                          );
                        })}
                        {!!onRowClick && (
                          <TableCell>
                            <RightIcon style={{ height: 16 }} />
                          </TableCell>
                        )}
                      </TableRow>
                      {isExpanded && renderExpandedRow && renderExpandedRow(row)}
                    </Fragment>
                  );
                })}
                {!tileTable && (
                  <EmptyRows rowsPerPage={DEFAULT_ROWS_PER_PAGE} rowsDisplayLength={rowsDisplay.length} rowHeight={emptyRowHeight} />
                )}
              </>
            )}
          </TableBody>
          <TableHead style={{ position: 'sticky', top: 0 }}>
            <TableRow>
              {!!renderExpandedRow && (
                <TableCell
                  width={50}
                  style={{
                    borderTop: tileTable ? '1px solid #e0e0e0' : 'none',
                  }}
                ></TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={`header-${String(column.field)}-${column.headerName}`}
                  sortDirection={orderBy === column.field ? order : false}
                  style={{
                    width: column.width,
                    height: tileTable ? 40 : undefined,
                    padding: tileTable ? '6px 12px' : undefined,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: 120,
                    borderTop: tileTable ? '1px solid #e0e0e0' : 'none',
                    verticalAlign: 'top',
                    lineHeight: 1.1,
                  }}
                  data-cy={`header-${String(column.field)}`}
                >
                  {column.sortable === false ? (
                    <TableColumnHeader column={column} />
                  ) : (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : undefined}
                      onClick={() => {
                        let orderDir: Order = 'asc';
                        if (orderBy === column.field) {
                          orderDir = order === 'asc' ? 'desc' : undefined;
                        }
                        setOrder(orderDir);
                        setOrderBy(orderDir ? column.field : '');
                      }}
                    >
                      <TableColumnHeader column={column} />
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
              {!!onRowClick && (
                <TableCell
                  width={50}
                  style={{
                    borderTop: tileTable ? '1px solid #e0e0e0' : 'none',
                  }}
                ></TableCell>
              )}
            </TableRow>
          </TableHead>
          {!noFooter && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[DEFAULT_ROWS_PER_PAGE]}
                  count={rowsFiltered.length}
                  rowsPerPage={DEFAULT_ROWS_PER_PAGE}
                  page={page}
                  onPageChange={(e, page) => setPage(page)}
                  pageLabel={t('Page')}
                  ofLabel={t('of')}
                />
              </TableRow>
            </TableFooter>
          )}
        </WebSharedTable>
      </TableContainer>
    );
  };

export default Table;

const SKELETON_WIDTHS = [70, 45, 60, 85, 50, 75, 40, 90, 65, 55];

const SkeletonRows = ({ rowsPerPage, numOfColumns, onRowClick, renderExpandedRow, tileTable }) => {
  const skeletonRowHeight = 100 / rowsPerPage + '%';
  return (
    <>
      {generateArray(rowsPerPage).map((i) => (
        <TableRow key={`skeleton-row-${i}`} sx={{ height: skeletonRowHeight }}>
          {!!renderExpandedRow && (
            <TableCell>
              <RightIcon style={{ opacity: 0, height: 16 }} />
            </TableCell>
          )}
          {generateArray(numOfColumns).map((j) => (
            <TableCell key={`skeleton-cell-${j}-row-${i}`} style={tileTable ? { padding: '0 12px', height: skeletonRowHeight } : undefined}>
              <Skeleton width={`${SKELETON_WIDTHS[(i + j) % SKELETON_WIDTHS.length]}%`} height={tileTable ? 15 : undefined} />
            </TableCell>
          ))}
          {!!onRowClick && (
            <TableCell>
              <RightIcon style={{ opacity: 0, height: 16 }} />
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  );
};

const EmptyRows = ({ rowsPerPage, rowsDisplayLength, rowHeight }) => {
  return (
    <>
      {rowsDisplayLength < rowsPerPage &&
        generateArray(rowsPerPage - rowsDisplayLength).map((row) => (
          <TableRow key={`empty-row-${row}`} sx={{ height: rowHeight }}></TableRow>
        ))}
    </>
  );
};
